import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { WiseguyWidgetSettings, WiseguyColumn, HighlightRule, HighlightableColumn } from '../types/wiseguy';

interface WiseguySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: WiseguyWidgetSettings) => void;
  settings: WiseguyWidgetSettings;
  initialTab?: 'columns' | 'highlights';
}

const COLUMN_LABELS: Record<WiseguyColumn, string> = {
  time: 'Time',
  symbol: 'Symbol',
  type: 'C/P (Call / Put)',
  expiry: 'Expiry',
  dte: 'DTE (Days to Expiry)',
  strike: 'Strike',
  size: 'Premium (dollar notional)',
  mult: 'Multiplier (vol / ADV)',
  bias: 'Bias',
  flag: 'Type (sweep / trade / split)',
  side: 'Side (aggressor)',
  qty: 'Qty (contracts in print)',
  volume: 'Vol (session option volume)',
  iv: 'IV',
  ivChgPct: 'IVΔ%',
  last: 'Last (option trade price)',
  delta: 'Delta',
  oi: 'Open Interest',
  assetClass: 'Asset Class',
  refPrice: 'Spot (underlying)',
  condition: 'Condition (incl. event tags)',
  comment: 'Comment',
};

const HIGHLIGHTABLE_COLUMNS: { column: HighlightableColumn; label: string; placeholder: { min: string; max: string } }[] = [
  { column: 'size', label: 'Size', placeholder: { min: 'Min (e.g., 100000)', max: 'Max (e.g., 1000000)' } },
  { column: 'mult', label: 'Multiplier', placeholder: { min: 'Min (e.g., 2)', max: 'Max (e.g., 10)' } },
  { column: 'dte', label: 'DTE', placeholder: { min: 'Min (e.g., 0)', max: 'Max (e.g., 7)' } },
  { column: 'refPrice', label: 'Reference Price', placeholder: { min: 'Min (e.g., 50)', max: 'Max (e.g., 200)' } },
];

/**
 * Highlight swatches, harmonised with the Fuzion palette.
 *
 * ⚠️ The five stock Tailwind colours that used to sit here (#3b82f6, #8b5cf6,
 * #ec4899, #14b8a6, #f97316) belong to no token and read as a different system
 * against the rest of the app. These are the brand three plus six that sit
 * beside them.
 *
 * ⚠️ **Every default in `DEFAULT_VIEW_CONFIG.highlightRules` must appear in
 * this list.** The picker marks the active swatch by matching on value, so a
 * default that is not here renders with nothing selected — the rule looks
 * unconfigured while it is in fact applying a colour.
 */
const DEFAULT_COLORS = [
  '#1c76a2', // bull-blue
  '#d8483b', // bear-red
  '#c2a14e', // accent-yellow
  '#7b61e0', // purple
  '#f4f2ed', // bone
  '#5b7d97', // muted slate blue
  '#b06a3c', // clay
  '#dcc07a', // light gold
  '#3a9fd6', // sky blue
];

const DEFAULT_RULE: HighlightRule = {
  enabled: false,
  minValue: undefined,
  maxValue: undefined,
  color: '#C2A14E',
};

export function WiseguySettingsModal({
  isOpen,
  onClose,
  onSave,
  settings,
  initialTab = 'columns',
}: WiseguySettingsModalProps) {
  const [localSettings, setLocalSettings] = useState<WiseguyWidgetSettings>(settings);
  const [activeTab, setActiveTab] = useState<'columns' | 'highlights'>(initialTab);

  // Reset local settings and tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSettings(settings);
      setActiveTab(initialTab);
    }
  }, [isOpen, settings, initialTab]);

  if (!isOpen) return null;

  const handleColumnVisibilityChange = (column: WiseguyColumn, visible: boolean) => {
    setLocalSettings((prev) => ({
      ...prev,
      columnVisibility: {
        ...prev.columnVisibility,
        [column]: visible,
      },
    }));
  };

  const handleHighlightRuleChange = (
    column: HighlightableColumn,
    field: keyof HighlightRule,
    value: boolean | number | string | undefined
  ) => {
    setLocalSettings((prev) => ({
      ...prev,
      highlightRules: {
        ...prev.highlightRules,
        [column]: {
          ...(prev.highlightRules[column] || DEFAULT_RULE),
          [field]: value,
        },
      },
    }));
  };

  const getRule = (column: HighlightableColumn): HighlightRule => {
    return localSettings.highlightRules[column] || DEFAULT_RULE;
  };

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-surface border border-dark-border rounded-lg w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
          <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
            {activeTab === 'columns' ? 'Column Visibility' : 'Highlight Rules'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'columns' && (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 mb-3">
                Toggle which columns are visible in the table.
              </p>
              {(Object.keys(COLUMN_LABELS) as WiseguyColumn[]).map((column) => (
                <label
                  key={column}
                  className="flex items-center justify-between py-2 px-3 bg-dark-bg rounded hover:bg-dark-surface-light transition-colors cursor-pointer"
                >
                  <span className="text-sm text-gray-300">{COLUMN_LABELS[column]}</span>
                  <input
                    type="checkbox"
                    checked={localSettings.columnVisibility[column] !== false}
                    onChange={(e) => handleColumnVisibilityChange(column, e.target.checked)}
                    className="w-4 h-4 rounded bg-dark-surface border-dark-border text-accent-yellow focus:ring-accent-yellow focus:ring-offset-0"
                  />
                </label>
              ))}
            </div>
          )}

          {activeTab === 'highlights' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-400 mb-3">
                Highlight cells when values fall within a range. Leave min or max blank for open-ended ranges.
              </p>

              {HIGHLIGHTABLE_COLUMNS.map(({ column, label, placeholder }) => {
                const rule = getRule(column);
                return (
                  <div key={column} className="bg-dark-bg rounded-lg p-3 space-y-3">
                    <label className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-200">{label}</span>
                      <input
                        type="checkbox"
                        checked={rule.enabled}
                        onChange={(e) => handleHighlightRuleChange(column, 'enabled', e.target.checked)}
                        className="w-4 h-4 rounded bg-dark-surface border-dark-border text-accent-yellow focus:ring-accent-yellow focus:ring-offset-0"
                      />
                    </label>

                    {rule.enabled && (
                      <div className="space-y-3 pt-2 border-t border-dark-border">
                        {/* Range inputs */}
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder={placeholder.min}
                            value={rule.minValue ?? ''}
                            onChange={(e) =>
                              handleHighlightRuleChange(
                                column,
                                'minValue',
                                e.target.value ? Number(e.target.value) : undefined
                              )
                            }
                            className="flex-1 px-2 py-1.5 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                          />
                          <span className="text-gray-500 text-xs">to</span>
                          <input
                            type="number"
                            placeholder={placeholder.max}
                            value={rule.maxValue ?? ''}
                            onChange={(e) =>
                              handleHighlightRuleChange(
                                column,
                                'maxValue',
                                e.target.value ? Number(e.target.value) : undefined
                              )
                            }
                            className="flex-1 px-2 py-1.5 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                          />
                        </div>

                        {/* Color picker */}
                        <div className="space-y-1">
                          <label className="text-xs text-gray-400">Highlight Color</label>
                          <div className="flex items-center gap-1">
                            {DEFAULT_COLORS.map((color) => (
                              <button
                                key={color}
                                onClick={() => handleHighlightRuleChange(column, 'color', color)}
                                className={`w-6 h-6 rounded border-2 transition-all ${
                                  rule.color === color
                                    ? 'border-accent-yellow scale-110'
                                    : 'border-transparent hover:scale-105'
                                }`}
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Preview text */}
                        <div className="text-[10px] text-gray-500">
                          {rule.minValue !== undefined && rule.maxValue !== undefined
                            ? `Highlights when ${rule.minValue} ≤ value ≤ ${rule.maxValue}`
                            : rule.minValue !== undefined
                              ? `Highlights when value ≥ ${rule.minValue}`
                              : rule.maxValue !== undefined
                                ? `Highlights when value ≤ ${rule.maxValue}`
                                : 'Set min and/or max to enable highlighting'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-dark-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-xs font-semibold text-dark-bg bg-accent-yellow hover:bg-accent-yellow/85 rounded transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
